import { Device } from '@capacitor/device';

import { toNumber } from '../domain/points';
import { totalMax, totalScore, type Assessment } from '../domain/scoring';
import type { Exam } from '../domain/types';
import { appendAppLog } from '../platform/appLog';
import { geraeteName } from '../platform/appVersion';
import { browserGeraeteKennung } from '../platform/browserGeraet';
import { Signing } from '../platform/signing';
import { normalizeSheet, SHEET_KEY, type Sheet } from '../store/sheet';
import { candidateStatus, ensureClient } from './client';
import { letzteStation, setDeviceInfo } from './examLog';
import { listExams, readExam } from './readExam';
import { buildTaskIndex, loadAssessment, type TaskIndex } from './repos';
import type { CandidateExamMetadataRow, ClientRow } from './rows';
import { seedLogTypes } from './seed';
import { loadSettings } from './settings';
import { getStore } from './store';

/**
 * Beim Start: Ablage öffnen, Stammdaten sichern, Gerätezeile herstellen, Inhalte laden und
 * den Stand des zuletzt gewählten Prüflings zurücklesen.
 *
 * ⚠️ **Ein kaputter Datenstand darf den Start nicht blockieren.** Scheitert das Rücklesen,
 * kommt die App mit Vorgabewerten hoch; der Fehler steht im Dateilog.
 *
 * ⚠️ **Inhalt und Bewertung sind zwei getrennte Fehlerquellen, absichtlich in zwei
 * `try`-Blöcken.** Scheitert `readExam`/`buildTaskIndex`/`candidateStatus`, ist die Prüfung
 * selbst nicht lesbar — `exam: null` ist dann richtig, die Oberfläche hat nichts zu zeigen.
 * Scheitert dagegen nur `loadAssessment` (z. B. an einer defekten Handschriftdatei eines
 * einzelnen Prüflings), *ist* die Prüfung lesbar — sie stehen zu lassen und nur die Bewertung
 * leer zu geben, ist der Unterschied zwischen „der Prüfer kann mit der Prüfung arbeiten" und
 * „gar nichts geht mehr". Ein gemeinsamer `try` um beides hätte einen Fehler, der nur einen
 * Prüfling betrifft, zu einer für alle sichtbaren Störung aufgeblasen.
 *
 * ⚠️ **Die Aufgabenkennung ist beim Einlesen gewechselt, nicht hier.** Die Beispieldaten
 * (`EXAMS`) führen ihre Aufgaben unter kurzen, sprechenden Schlüsseln (`t1`, `t2`, …) —
 * praktisch für Testdaten, aber keine Relay-Kennung. `importExam` legt die eigentlichen
 * `ExamItem`-Zeilen an (Kennung `mockId('ExamItem', …)`, stabil über `uuidv5`), `readExam`
 * liest sie hier mit **dieser** Kennung zurück, und `buildTaskIndex` ist die Brücke zu
 * `ExamLog`. Weil `mockId` von den Ausgangsdaten und nicht vom Zufall abhängt, trifft ein
 * zweites Einlesen dieselben Kennungen wie das erste — eine mit `saveAssessment` geschriebene
 * Bewertung bleibt darüber hinweg auffindbar, siehe `hydrate.test.ts` („Ein vor dem
 * Wiedereinstieg gesicherter Bogen bleibt auffindbar").
 *
 * ⚠️ **Der gemerkte Prüfling wird gegen die geladenen Prüflinge geprüft.** `Client.
 * currentCandidateID` kann auf eine Person zeigen, die in einem inzwischen neu geladenen
 * Prüfungsinhalt nicht mehr vorkommt (`importExam` ersetzt die Inhalte). In dem Fall gilt der
 * Start ausdrücklich **nicht** als Wiedereinstieg — sonst stünde `resumed: true` samt Hinweis
 * für einen Prüfling, den es nicht mehr gibt.
 */

export interface HydrationResult {
  client: ClientRow;
  settings: Record<string, string>;
  exam: Exam | null;
  index: TaskIndex;
  assessment: Assessment | null;
  /**
   * Die Station, an der der gemerkte Prüfling zuletzt begonnen hat — aus dem Protokoll, nicht
   * geraten. `null`, wenn es keinen gemerkten Prüfling gibt oder kein `CONTAINER_START` zu
   * ihm steht. Siehe `examLog.ts::letzteStation`.
   */
  lastStationId: string | null;
  /** Zustand je Prüfling aus `CandidateExamMetadata`, Schlüssel ist die Candidate-Kennung. */
  statuses: Record<string, CandidateExamMetadataRow['status']>;
  /**
   * Die auf diesem Gerät abgesendeten Prüflinge (`SUBMITTED`) — die Sperre überlebt so den
   * Neustart (Audit 2026-10-11, Fund 2). Uhrzeit aus `lastModifiedDate`, Punkte aus dem Bogen.
   */
  abgesendet: Record<string, { at: string; total: number; max: number }>;
  /** Ihre Bögen, damit ein Wechsel zu ihnen nicht leer aufgeht (Fund 1). */
  boegen: Record<string, Assessment>;
  sheet: Sheet;
  /** Ob ein Stand vorlag — dann bekommt der Prüfer den kurzen Hinweis. */
  resumed: boolean;
  /**
   * Ob eine **vorhandene** Prüfung nicht gelesen werden konnte.
   *
   * ⚠️ **Nicht dasselbe wie `exam === null`.** Seit die Beispielprüfungen nur noch auf
   * Knopfdruck kommen, ist „keine Prüfung auf dem Gerät" der gewöhnliche Anfangszustand —
   * die App kommt dann ganz normal hoch und zeigt die (leere) Auswahl. Nur wenn eine
   * Prüfung da ist und sich nicht lesen lässt, ist etwas kaputt, und nur dann zeigt
   * `main.tsx` statt der Oberfläche die Meldung `persistence.startupFailed`.
   */
  leseFehler: boolean;
}

/** Ob `exam` eine Aufgabe mit dieser Kennung führt — über alle Stationen hinweg. */
function hatAufgabe(exam: Exam | null, taskId: string): boolean {
  if (!exam) return false;
  return exam.stations.some((station) => station.tasks.some((task) => task.id === taskId));
}

/** Ob `exam` einen Prüfling mit dieser Kennung führt. */
function hatKandidat(exam: Exam, candidateId: string): boolean {
  return exam.candidates.some((kandidat) => kandidat.id === candidateId);
}

/**
 * Die Stage-ID aus dem Schlüsselbund — dieselbe, mit der sich das Gerät bei Relay und Classic
 * anmeldet und die in den Manifesten steht. Bis 2026-10-10 trug die Gerätezeile die Hersteller-ID
 * (`Device.getId()`, wechselt mit den Apps des Anbieters), und Classic fand sie über die Stage-ID
 * nie: Prüfer und Station fehlten im Herzschlag.
 *
 * Antwortet der Schlüsselbund nicht, behält eine vorhandene Zeile ihre ID — nie eine neue
 * erfinden. Nur ein Gerät ganz ohne Zeile fällt auf die Hersteller-ID zurück (im Browser leer,
 * dann `browserGeraeteKennung()`); der nächste Start mit Schlüsselbund stellt es um.
 */
async function geraeteId(bisher: ClientRow | undefined): Promise<string> {
  try {
    const { deviceId } = await Signing.getIdentity();
    if (deviceId) return deviceId;
  } catch (fehler) {
    await appendAppLog('Geräte-ID nicht lesbar: ' + String(fehler), 'warn').catch(() => undefined);
  }
  if (bisher) return bisher.deviceIdentifier;
  return (await Device.getId()).identifier || browserGeraeteKennung();
}

/**
 * Die auf diesem Gerät abgesendeten Prüflinge **einer** Prüfung samt Bögen — beim Start und
 * beim Prüfungswechsel (`useAppStore.ts::selectExam`). Die Sperre ist nur nach Prüfling
 * geschlüsselt; ohne Neuladen beim Wechsel sperrte das Abgesendet der alten Prüfung denselben
 * Prüfling in der neuen (gefunden 2026-11-02, Relay nutzt den Beispielprüfling wieder).
 */
export async function abgesendeteBoegen(
  exam: Exam,
  index: TaskIndex,
  examId: string,
): Promise<Pick<HydrationResult, 'abgesendet' | 'boegen'>> {
  const store = getStore();
  const abgesendet: HydrationResult['abgesendet'] = {};
  const boegen: HydrationResult['boegen'] = {};
  const metadaten = await store.all<CandidateExamMetadataRow>('CandidateExamMetadata', { examID: examId });
  for (const zeile of metadaten) {
    if (zeile.status !== 'SUBMITTED') continue;
    try {
      const stationId = await letzteStation(store, examId, zeile.candidateID);
      const bogen = await loadAssessment(store, index, examId, zeile.candidateID, stationId ?? undefined);
      // Ohne bekannte Station zählen alle Aufgaben — ein Gerät bewertet meist nur eine.
      const tasks = exam.stations.find((s) => s.id === stationId)?.tasks ?? exam.stations.flatMap((s) => s.tasks);
      const zeit = zeile.lastModifiedDate ?? '';
      boegen[zeile.candidateID] = bogen;
      abgesendet[zeile.candidateID] = {
        at: zeit.slice(8, 10) + ':' + zeit.slice(10, 12),
        total: toNumber(totalScore(tasks, bogen)),
        max: toNumber(totalMax(tasks)),
      };
    } catch (fehler) {
      await appendAppLog('Abgesendete Bewertung nicht lesbar: ' + String(fehler), 'error');
    }
  }
  return { abgesendet, boegen };
}

export async function hydrate(): Promise<HydrationResult> {
  const store = getStore();
  await store.open();
  await seedLogTypes(store);

  const gerät = await Device.getInfo();
  const [bisher] = await store.all<ClientRow>('Client');
  const kennung = await geraeteId(bisher);
  if (bisher && bisher.deviceIdentifier !== kennung) {
    await appendAppLog(`Geräte-ID der Gerätezeile: ${bisher.deviceIdentifier} → ${kennung}`).catch(() => undefined);
  }
  const client = await ensureClient(store, {
    deviceIdentifier: kennung,
    // Die Vorgabe der Plattform („iPad", Rechnername, Browser + Zufallsname), nicht der
    // private Name aus den iOS-Einstellungen (Vorgabe des Nutzers, 2026-10-08).
    deviceName: geraeteName() || (gerät.name ?? gerät.model),
    clientType: gerät.platform === 'web' ? 'BROWSER' : 'TABLET',
    appVersion: null,
    osVersion: gerät.osVersion,
  });
  setDeviceInfo({ deviceIdentifier: kennung, deviceName: geraeteName() || (gerät.name ?? gerät.model) });

  const settings = await loadSettings(store, client.id);

  /*
   * ⚠️ **Hier werden keine Beispielprüfungen mehr eingelesen** (Entscheidung des Nutzers,
   * 2026-09-12). Ein Gerät ohne Prüfung bleibt leer, bis eine vom Server kommt oder der
   * Prüfer in den Einstellungen „Demo-Prüfungen importieren" drückt
   * (`server/zugang.ts::ladeBeispielpruefungen`).
   *
   * Der Reihe nach gab es hier drei Fassungen, und jede hatte ihren Grund:
   *
   * 1. Zuerst wurde **je Prüfung** geprüft (`isExamLoaded`) und nachgeladen — wer eine
   *    Beispielprüfung löschte, hatte sie nach dem nächsten Start wieder.
   * 2. Dann nur noch auf einem Gerät ohne jede Prüfung und ohne eingerichteten Server. Das
   *    band den Demo-Inhalt an eine Frage, mit der er nichts zu tun hat: auf dem iPad ist
   *    `istEingerichtet` **immer** wahr (s. `VORGABE` in `server/zugang.ts`), dort kamen die
   *    Beispiele also ohnehin nie.
   * 3. Jetzt gar nicht mehr. Was auf dem Gerät liegt, hat jemand dorthin geholt.
   */

  // Fällt die gemerkte Prüfung weg (gelöscht, nie geladen), nimmt die App die erste
  // vorhandene — und wenn es keine gibt, bleibt `examId` leer und `readExam` unten scheitert
  // ordentlich, statt auf eine Beispielkennung zu zeigen, die es nicht mehr gibt.
  // Nach Namen sortiert, damit die Wahl nicht von der Reihenfolge der Ablage abhängt — die
  // ist beim Browser-Adapter die Kennung und damit für einen Menschen zufällig.
  const vorhandene = [...(await listExams(store))].sort((a, b) => a.name.localeCompare(b.name));
  const examId = client.currentExamID ?? vorhandene[0]?.id ?? '';
  let exam: Exam | null = null;
  let index: TaskIndex = new Map();
  let assessment: Assessment | null = null;
  let lastStationId: string | null = null;
  let statuses: Record<string, CandidateExamMetadataRow['status']> = {};
  let resumed = false;
  let leseFehler = false;

  // ⚠️ **Leere Kennung ist kein Fehler, sondern Leere.** `readExam('')` würfe, und der `catch`
  // darunter machte daraus eine Störungsmeldung — auf einem Gerät, dem schlicht noch niemand
  // eine Prüfung gegeben hat. Deshalb wird gar nicht erst gelesen.
  if (examId !== '') {
    try {
      exam = await readExam(store, examId);
      index = await buildTaskIndex(store, examId);
      statuses = await candidateStatus(store, examId);
    } catch (fehler) {
      await appendAppLog('Prüfung konnte nicht gelesen werden: ' + String(fehler), 'error');
      exam = null;
      index = new Map();
      statuses = {};
      leseFehler = true;
    }
  }

  // Getrennt vom Block oben: ein Fehlschlag hier darf die bereits gelesene Prüfung nicht
  // wieder wegwerfen (s. Doku-Kommentar oben). Ebenso gilt ein gemerkter Prüfling, der in den
  // (möglicherweise neu geladenen) Prüfungsinhalten nicht mehr vorkommt, ausdrücklich nicht
  // als Wiedereinstieg.
  const gemerkterKandidat = client.currentCandidateID;
  if (exam && gemerkterKandidat && hatKandidat(exam, gemerkterKandidat)) {
    try {
      // ⚠️ **Erst die Station, dann die Bewertung.** `loadAssessment` holt den
      // Stationskommentar nur, wenn es weiss, an welcher Station bewertet wurde — und das
      // steht im Protokoll, nicht im Bogen.
      lastStationId = await letzteStation(store, examId, gemerkterKandidat);
      assessment = await loadAssessment(store, index, examId, gemerkterKandidat, lastStationId ?? undefined);
      resumed = true;
    } catch (fehler) {
      await appendAppLog('Bewertung konnte nicht gelesen werden: ' + String(fehler), 'error');
      assessment = null;
      lastStationId = null;
      resumed = false;
    }
  }

  let sheet: Sheet = { kind: 'none' };
  try {
    sheet = normalizeSheet(JSON.parse(localStorage.getItem(SHEET_KEY) ?? 'null'));
  } catch {
    sheet = { kind: 'none' };
  }

  // `normalizeSheet` kennt nur die Form des gespeicherten Blatts, nicht den Inhalt der Prüfung —
  // die Aufgabe könnte zwischen dem Sichern und diesem Start aus dem Inhalt gefallen sein (ein
  // neu heruntergeladenes Bündel ersetzt die Aufgaben, `importExam.ts`). Ein Kommentarblatt zu
  // einer Aufgabe, die es nicht mehr gibt, ginge sonst über einer leeren Fläche auf
  // (`App.tsx::ActiveSheet` findet die Aufgabe nicht und zeigt nichts) — hier fällt es auf
  // `none` zurück, solange die Aufgabe wirklich fehlt.
  if (sheet.kind === 'comment' && !hatAufgabe(exam, sheet.taskId)) {
    sheet = { kind: 'none' };
  }

  const { abgesendet, boegen } = exam
    ? await abgesendeteBoegen(exam, index, examId)
    : { abgesendet: {}, boegen: {} };

  return {
    client,
    settings,
    exam,
    index,
    assessment,
    lastStationId,
    statuses,
    abgesendet,
    boegen,
    sheet,
    resumed,
    leseFehler,
  };
}
